import { Navigate, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import {
  findDisplayNextLevel,
  useTrainingAttempt,
  useTrainingModuleSummary,
  useTrainingSimulatorHome,
  type TrainingCompletionScreen,
} from "@/lib/api/training-simulator/training-simulator.ts";
import { useContinueToNextTrainingLevel } from "@/lib/hooks/training-simulator/use-enter-training-session.ts";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events.ts";
import { assertNever } from "@/lib/utils.ts";
import { TrainingModuleComplete } from "./training-module-complete.tsx";
import { TrainingAllModulesComplete } from "./training-all-modules-complete.tsx";

interface TrainingCompletionProps {
  attemptId: string;
  screenOverride?: TrainingCompletionScreen;
}

export function TrainingCompletion({
  attemptId,
  screenOverride,
}: TrainingCompletionProps) {
  const navigate = useNavigate();
  const { track } = useTrackEvent();
  const { data: attempt, isLoading, isError } = useTrainingAttempt(attemptId);
  const { data: home } = useTrainingSimulatorHome();
  const { continueToNextLevel, isContinuingToNextLevel } =
    useContinueToNextTrainingLevel();

  const screen = screenOverride ?? attempt?.completion_screen;
  const completedModuleId = attempt?.level.module.id;
  // The summary is generated lazily by the API on first request, so we only
  // ask for it on the module screen; the query's loading state drives the
  // Module Complete loading copy.
  const { data: moduleSummary } = useTrainingModuleSummary(
    screen === "module" ? completedModuleId : undefined,
  );

  if (isLoading) {
    return (
      <div
        role="status"
        className="flex min-h-screen w-full items-center justify-center bg-surfaces-z3"
      >
        <Loader2 className="size-10 animate-spin text-text-secondary" />
      </div>
    );
  }

  if (isError || !attempt) {
    return (
      <div
        role="alert"
        className="flex min-h-screen w-full items-center justify-center bg-surfaces-z3"
      >
        <p className="text-body-1 text-text-secondary">
          Something went wrong loading this screen. Please try again.
        </p>
      </div>
    );
  }

  // This page only owns the full-screen module/simulator states. A level
  // completion is a modal inside the session, and an unresolved screen (null
  // completion_screen, e.g. a stale/direct visit) has nothing to render here —
  // both send the user back to the session as the safe home base.
  if (screen !== "module" && screen !== "simulator") {
    return (
      <Navigate
        to="/training/session/$attemptId"
        params={{ attemptId }}
        replace
      />
    );
  }

  const goHome = () => {
    track(PostHogEvents.SIMULATOR_BACK_TO_TRAINING_CLICK, {
      source: attempt.completion_screen,
    });
    navigate({ to: "/training" });
  };

  switch (screen) {
    case "module": {
      const nextLevelMatch = findDisplayNextLevel(home, attempt.level.id);
      const nextModule = nextLevelMatch
        ? {
            position: nextLevelMatch.module.position,
            title: nextLevelMatch.module.title,
            description: nextLevelMatch.module.description,
          }
        : null;

      return (
        <TrainingModuleComplete
          modulePosition={attempt.level.module.position}
          moduleTitle={attempt.level.module.title}
          summary={moduleSummary?.summary ?? null}
          nextModule={nextModule}
          isContinuing={isContinuingToNextLevel}
          onContinue={continueToNextLevel}
          onBackToTraining={goHome}
        />
      );
    }
    case "simulator":
      return <TrainingAllModulesComplete onBackToTraining={goHome} />;
    default:
      return assertNever(screen);
  }
}
